import { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faChalkboardUser,
  faChartLine,
  faHeart,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../auth/AuthContext";
import { resolvePostLoginRoute } from "../auth/post-login-route";
import { LEGAL_SUPPORT_EMAIL } from "./legal/legal-meta";
import SiteFooter from "./legal/SiteFooter";
import LandingNav from "../marketing/LandingNav";
import FeatureCarousel, { type FeatureCarouselSlide } from "../marketing/FeatureCarousel";
import AboutMediaFrame from "../marketing/AboutMediaFrame";
import { AboutMediaLightbox } from "../marketing/AboutMediaFrame";
import {
  ABOUT_STORY_MEDIA,
  ABOUT_PRODUCT_DEMO_VIDEO,
  carouselMedia,
  spotlightMedia,
} from "../marketing/about-media";
import {
  TEACHER_PAID_FEATURES_LINE,
  TEACHER_TRIAL_SUMMARY,
} from "../marketing/teacher-billing-copy";

const SPOTLIGHTS: readonly {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  reverse?: boolean;
}[] = [
  {
    id: "ai-lessons",
    eyebrow: "AI lesson studio",
    title: "Bring your style. Let AI handle the scaffolding.",
    body: "Describe what students should learn in plain English. Code Create Slay drafts starter code, a step-by-step guided tour, and automated checks. You edit, refine, and teach in your own voice without spending evenings mastering advanced Python just to build dozens of lessons.",
  },
  {
    id: "classroom-ide",
    eyebrow: "The classroom IDE",
    title: "Real Python, right in the browser",
    body: "Students write actual code on Chromebooks with no installs. Beginners follow a guided tour in a focused editor. When they are ready, the same workspace grows into multi-file projects. They learn a real language, not drag-and-drop blocks.",
    reverse: true,
  },
  {
    id: "progress",
    eyebrow: "Celebrate wins. Spot the stuck spots.",
    title: "Know who is cruising and who needs you",
    body: "Live cohort dashboards show completion, in-progress work, and where students get stuck lesson by lesson. Celebrate when they succeed, then jump in where it matters instead of guessing from the front of the room.",
  },
];

const CAROUSEL_SLIDES: readonly FeatureCarouselSlide[] = [
  {
    id: "library",
    eyebrow: "Lesson library",
    title: "Build once, teach every term",
    body: "Every lesson lands in a searchable library. Bundle them into reusable course blueprints and run the same strong curriculum across multiple years without rebuilding from scratch.",
    media: carouselMedia("library"),
  },
  {
    id: "cohorts",
    eyebrow: "Cohorts",
    title: "Several classes, each with its own rhythm",
    body: "Run multiple cohorts side by side with separate rosters, join codes, and release schedules. One teacher account, many classes, each moving at its own pace.",
    media: carouselMedia("cohorts"),
  },
  {
    id: "courses",
    eyebrow: "Courses",
    title: "Ordered blueprints from your library",
    body: "Line up lessons into a course template once. When a new term starts, spin up a cohort from that blueprint and release assignments on your schedule.",
    media: carouselMedia("courses"),
  },
  {
    id: "playgrounds",
    eyebrow: "Playgrounds",
    title: "Room to experiment beyond assignments",
    body: "Students and teachers get free-form coding projects when they want to tinker, remix, or stretch past the day's lesson.",
    media: carouselMedia("playgrounds"),
  },
  {
    id: "students-free",
    eyebrow: "Always free for students",
    title: "Kids never see a paywall",
    body: `Students sign in with Google and learn for free, forever. Teachers start with ${TEACHER_TRIAL_SUMMARY.toLowerCase()} Subscribe when you are ready for ${TEACHER_PAID_FEATURES_LINE.toLowerCase()}, and full teaching tools. Payments run securely through Stripe, we never see your card details, and you can cancel anytime.`,
    media: carouselMedia("students-free"),
  },
];

function Section({
  id,
  eyebrow,
  title,
  lede,
  children,
  tone,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  lede?: string;
  children: React.ReactNode;
  tone?: "tint";
}) {
  return (
    <section id={id} className={`landing-section${tone === "tint" ? " landing-section--tint" : ""}`}>
      <div className="landing-section-inner">
        <header className="landing-section-head">
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="landing-section-title">{title}</h2>
          {lede ? <p className="landing-section-lede">{lede}</p> : null}
        </header>
        {children}
      </div>
    </section>
  );
}

export default function AboutPage() {
  const { status, profile } = useAuth();
  const authed = status !== "anonymous";
  const [storyLightboxOpen, setStoryLightboxOpen] = useState(false);
  const [spotlightLightboxId, setSpotlightLightboxId] = useState<string | null>(null);

  const appHref =
    profile?.role === "teacher"
      ? "/teacher"
      : profile
        ? resolvePostLoginRoute(profile)
        : "/onboarding";

  return (
    <div className="landing about-page">
      <LandingNav />

      <main>
        <section className="landing-hero about-hero">
          <div className="landing-hero-inner about-hero-inner">
            <div className="landing-hero-text">
              <p className="eyebrow">About Code Create Slay</p>
              <h1 className="landing-hero-title">Built for Ms. Leonard. Built for you.</h1>
              <p className="landing-hero-lede about-hero-lede">
                A browser-based Python classroom that helps dedicated teachers run real coding classes
                without sacrificing their evenings, weekends, or work-life balance.
              </p>
            </div>
            <div className="about-hero-badge" aria-hidden="true">
              <FontAwesomeIcon icon={faHeart} />
              <span>Made with love by Mr. Leonard for Ms. Leonard and her students</span>
            </div>
          </div>
        </section>

        <Section
          id="why"
          eyebrow="Why we built it"
          title="For a teacher who gives everything"
          tone="tint"
        >
          <div className="about-story">
            <div className="about-story-copy legal-body">
              <p>
                My wife is a dedicated math teacher with five years in the classroom
                who commutes two hours each day, five days a week. This year she&apos;s teaching a
                computer science course: the same skills that built my own career. I wanted to support
                her as she brings them to her students.
              </p>
              <p>
                I want her and her students to succeed and pick up genuinely cool skills along the way,
                without her pouring tons of energy into learning advanced concepts just to build dozens
                of lessons across multiple years. I want her to bring her energy and her style. I wanted
                a system that guides students, teaches real concepts, celebrates them when they succeed,
                and makes it obvious where they get stuck.
              </p>
              <p>
                So I built this site to help my wife teach my favorite subject with my favorite
                programming language, and still have a work-life balance of her own. The kids call her
                &ldquo;Ms. Leonard.&rdquo; I call her my wife and partner.
              </p>
            </div>
            <AboutMediaFrame
              item={ABOUT_STORY_MEDIA}
              className="about-story-shot"
              onClick={() => setStoryLightboxOpen(true)}
            />
          </div>
        </Section>

        <Section
          id="demo"
          eyebrow="See it in action"
          title="Five-minute product demo"
          lede="A quick walkthrough of how teachers build lessons, run cohorts, and watch students grow."
        >
          <div className="about-video-shell">
            <video
              className="about-video-player"
              controls
              playsInline
              preload="metadata"
              src={ABOUT_PRODUCT_DEMO_VIDEO}
            >
              Your browser does not support embedded video. You can{" "}
              <a href={ABOUT_PRODUCT_DEMO_VIDEO}>download the demo</a> instead.
            </video>
          </div>
        </Section>

        <Section
          id="spotlights"
          eyebrow="Feature spotlights"
          title="What makes Code Create Slay different"
          lede="The big ideas we designed around: less prep grind for teachers, more real coding for students."
          tone="tint"
        >
          <p className="about-screenshot-note muted small">
            Screenshots below are real captures from the product. We&apos;re still iterating quickly, so
            some details may look slightly out of date.
          </p>
          <div className="about-spotlights">
            {SPOTLIGHTS.map((spotlight) => {
              const media = spotlightMedia(spotlight.id);
              return (
                <article
                  key={spotlight.id}
                  className={`about-spotlight${spotlight.reverse ? " about-spotlight--reverse" : ""}`}
                >
                  <div className="about-spotlight-copy">
                    <p className="eyebrow">{spotlight.eyebrow}</p>
                    <h3 className="about-spotlight-title">{spotlight.title}</h3>
                    <p className="about-spotlight-body">{spotlight.body}</p>
                  </div>
                  <AboutMediaFrame
                    item={media}
                    className="about-spotlight-shot"
                    onClick={() => setSpotlightLightboxId(spotlight.id)}
                  />
                </article>
              );
            })}
          </div>
        </Section>

        <Section
          id="carousel"
          eyebrow="More to explore"
          title="Features worth a closer look"
          lede="Swipe through the rest of the toolkit. Tap any screenshot to open a full-screen gallery."
        >
          <p className="about-screenshot-note muted small">
            Real product screenshots. We ship often, so a label or button here and there may have moved
            since capture.
          </p>
          <FeatureCarousel slides={CAROUSEL_SLIDES} />
        </Section>

        <section className="landing-final-cta about-signoff">
          <div className="landing-final-cta-inner">
            <span className="about-signoff-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faChalkboardUser} />
            </span>
            <h2 className="landing-final-cta-title">Let&apos;s teach some kids!</h2>
            <p className="landing-final-cta-lede about-signoff-lede">
              I hope this tool helps you teach kids a real language. If you have feedback or feature
              ideas, I&apos;d love to hear them. My name&apos;s Tristan. My wife is Ms. Leonard.
            </p>
            <div className="about-signoff-actions">
              <a className="btn primary landing-cta" href={`mailto:${LEGAL_SUPPORT_EMAIL}`}>
                Send feedback <FontAwesomeIcon icon={faArrowRight} />
              </a>
              <Link className="btn landing-cta about-signoff-app-btn" to={authed ? appHref : "/login"}>
                {authed ? "Open the app" : "Try it free"}
              </Link>
            </div>
            <p className="about-signoff-features small">
              <FontAwesomeIcon icon={faWandMagicSparkles} /> AI-assisted lessons
              <span aria-hidden="true"> · </span>
              <FontAwesomeIcon icon={faChartLine} /> Live progress
              <span aria-hidden="true"> · </span>
              Free for students
            </p>
          </div>
        </section>

        <div className="landing-footer-wrap">
          <SiteFooter />
        </div>
      </main>

      <AboutMediaLightbox
        item={ABOUT_STORY_MEDIA}
        open={storyLightboxOpen}
        onClose={() => setStoryLightboxOpen(false)}
      />

      <AboutMediaLightbox
        item={spotlightLightboxId ? spotlightMedia(spotlightLightboxId) : ABOUT_STORY_MEDIA}
        open={spotlightLightboxId !== null}
        onClose={() => setSpotlightLightboxId(null)}
      />
    </div>
  );
}
